import { CSSProperties } from 'react';

/**
 * Shared visual language for list pages (Orders/Photographers/Customers/
 * Discounts), matching admin-dashboard.html's pillbar/chip/list-table look
 * — count pill next to the title, chip-style filters, a boxed search
 * input, and a muted-uppercase table header. Pure styling, no behavior:
 * kept as plain style objects/functions (not components) since these pages
 * are class components using inline styles throughout already — this just
 * centralizes the constants so all 4 pages stay visually consistent.
 *
 * Reuses this theme's existing primary accent (#0090e7, see
 * assets/scss/modern-dark/_variables.scss) rather than introducing a new
 * color — the redesign brief was to match structure/features, not change
 * the color theme.
 */

export const ACCENT = '#0090e7';
export const ACCENT_SOFT = 'rgba(0,144,231,0.14)';

export const countPillStyle: CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    padding: '3px 11px',
    borderRadius: 20,
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.7)',
    marginLeft: 10,
    verticalAlign: 'middle',
};

export const chipBarStyle: CSSProperties = {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: 16,
};

export const pillBarStyle: CSSProperties = {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 16,
};

/** Bigger/bolder than chipStyle — for primary tab-like toggles (All vs
 * Pending, All vs New-this-week), not secondary status filters. */
export function pillStyle(active: boolean): CSSProperties {
    return {
        border: `1.5px solid ${active ? ACCENT : 'rgba(255,255,255,0.15)'}`,
        background: active ? ACCENT : 'rgba(255,255,255,0.04)',
        color: active ? '#fff' : 'rgba(255,255,255,0.75)',
        fontSize: 13,
        fontWeight: 800,
        padding: '10px 18px',
        borderRadius: 11,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    };
}

export function chipStyle(active: boolean): CSSProperties {
    return {
        border: `1px solid ${active ? ACCENT : 'rgba(255,255,255,0.12)'}`,
        background: active ? ACCENT_SOFT : 'rgba(255,255,255,0.03)',
        color: active ? ACCENT : 'rgba(255,255,255,0.65)',
        padding: '6px 14px',
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    };
}

export const toolbarRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 16,
};

export const searchBoxWrapperStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 11,
    background: 'rgba(255,255,255,0.03)',
    flex: '1 1 260px',
    maxWidth: 360,
};

/** Applied to every <th> in a restyled list table — muted, small, uppercase. */
export const thStyle: CSSProperties = {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.45)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '10px 12px',
    whiteSpace: 'nowrap',
};
